import React, { useState, useMemo, useCallback, useRef } from 'react';
import AppWindowLayer from './AppWindowLayer';
import BirthdayOverlay from './BirthdayOverlay';
import Dock from './Dock';
import TopBar, { TOP_BAR_HEIGHT } from './Desktop/TopBar';
import './Desktop.css';
import VideoPlayer from './VideoPlayer';
import MusicPlayer from './MusicPlayer';
import BioWindow from './BioWindow';
import PhotoViewer from './PhotoViewer';
import NewsTerminal from './NewsTerminal';
import WebBrowser from './WebBrowser';
import Articles from './Articles';

// Import your animated webp icons
import musicIcon from './assets/icons/j2nz.webp';
import videoIcon from './assets/icons/dvdplayer.webp';
import bioIcon from './assets/icons/playerselect.webp';
import photoIcon from './assets/icons/viewmaster.webp';
import tshirtsIcon from './assets/icons/tshirt.webp';
import articlesIcon from './assets/icons/articles.webp';
import { TICKETS_URL } from './eventLinks';

// Dock-only external link (not a window): the old T-Shirts icon, now "Shop",
// opens the Neutrals x Normal ticket page in a new tab.
const SHOP_LINK = {
  id: 'shop',
  title: 'Shop',
  icon: <img src={tshirtsIcon} alt="" className="dock-icon-img" />,
  href: TICKETS_URL,
  ariaLabel: 'Shop: buy tickets to Neutrals x Normal on Eventbrite (opens in a new tab)',
};

const Desktop = () => {
  const [openApps, setOpenApps] = useState(['tour', 'video']);
  const managerRef = useRef(null);
  const openAppsRef = useRef(openApps);
  openAppsRef.current = openApps;

  const open = useCallback(id => {
    if (openAppsRef.current.includes(id)) {
      managerRef.current?.focus(id);
    } else {
      setOpenApps(prev => [...prev, id]);
    }
  }, []);

  const close = useCallback(id => {
    setOpenApps(prev => prev.filter(i => i !== id));
  }, []);

  const apps = useMemo(() => {
    const base = [
      {
        id: 'music',
        title: 'J2nZ',
        icon: <img src={musicIcon} alt="J2nZ" className="dock-icon-img" />,
        content: <MusicPlayer />,
        defaultWidth: 380,
        defaultHeight: 550,
      },
      {
        id: 'video',
        title: 'DVD Player',
        icon: <img src={videoIcon} alt="DVD Player" className="dock-icon-img" />,
        content: <VideoPlayer autoPlay={process.env.REACT_APP_DISABLE_VIDEO_AUTOPLAY !== 'true'} />,
        defaultWidth: 550,
        defaultHeight: 420,
        defaultY: 70,
      },
      {
        id: 'bio',
        title: 'Player Select',
        icon: <img src={bioIcon} alt="Player Select" className="dock-icon-img" />,
        content: <BioWindow />,
        defaultWidth: 720,
        defaultHeight: 540,
      },
      {
        id: 'photo',
        title: 'View Master',
        icon: <img src={photoIcon} alt="View Master" className="dock-icon-img" />,
        content: <PhotoViewer />,
        defaultWidth: 400,
        defaultHeight: 550,
      },
      {
        id: 'tour',
        title: 'News',
        icon: null,
        content: <NewsTerminal />,
        defaultWidth: 560,
        defaultHeight: 490,
      },
    ];

    const params = new URLSearchParams(window.location.search);
    // Hidden per the Aug 18, 2026 site-direction decision (keep the code,
    // don't delete it) — pass ?enableArticles=true to bring it back.
    if (params.get('enableArticles') === 'true') {
      base.push({
        id: 'articles',
        title: 'Articles',
        icon: <img src={articlesIcon} alt="Articles" className="dock-icon-img" />,
        content: <Articles />,
        defaultWidth: 380,
        defaultHeight: 280,
      });
    }
    if (params.get('enableBrowser') === 'true') {
      base.push({
        id: 'moshzilla',
        title: 'Moshzilla',
        icon: <div alt="Moshzilla" className="dock-icon-img">🌐</div>,
        content: <WebBrowser />,
        defaultWidth: 380,
        defaultHeight: 320,
      });
    }
    return base;
  }, []);

  const dockApps = useMemo(() => [...apps.filter(a => a.icon), SHOP_LINK], [apps]);
  const onNewsClick = useCallback(() => open('tour'), [open]);

  return (
    <div className="desktop">
      {process.env.REACT_APP_SHOW_BIRTHDAY === 'true' && <BirthdayOverlay />}
      <Dock apps={dockApps} onOpenApp={open} />
      <div className="desktop-content" style={{ top: TOP_BAR_HEIGHT }}>
        {/* Render AppWindows for all open apps */}
        <AppWindowLayer
          ref={managerRef}
          apps={apps}
          openApps={openApps}
          onCloseApp={close}
        />
      </div>
      <TopBar onNewsClick={onNewsClick} />
    </div>
  );
};

export default Desktop;
